import { supabase } from '../../config/supabaseClient';
import { ENV } from '../../config/env';

export interface WorkloadCapacity {
  id: string;
  organization_id: string;
  user_id: string;
  weekly_capacity_hours: number;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface WorkloadAllocation {
  id: string;
  organization_id: string;
  user_id: string;
  project_id?: string | null;
  start_date: string;
  end_date: string;
  allocation_percent: number;
  allocation_hours: number;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

async function getToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return session.access_token;
}

async function parseJsonSafe(res: Response): Promise<any> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

const API_BASE_URL = `${ENV.API_URL}/api/project-management/workload`;

export const workloadService = {
  async listCapacities(params: { organization_id: string; user_id?: string }): Promise<{ data: WorkloadCapacity[]; error: any }> {
    try {
      const token = await getToken();
      const qs = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => { if (v) qs.set(k, String(v)); });

      const res = await fetch(`${API_BASE_URL}/capacities?${qs.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const json = await parseJsonSafe(res);
      if (!res.ok) return { data: [], error: json?.error || 'Failed to fetch capacities' };
      return { data: (json?.data || []) as WorkloadCapacity[], error: null };
    } catch (e: any) {
      return { data: [], error: e?.message || 'Network error' };
    }
  },

  async upsertCapacity(params: { organization_id: string; user_id: string; weekly_capacity_hours: number; notes?: string | null }): Promise<{ data: WorkloadCapacity | null; error: any }> {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/capacities`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });
      const json = await parseJsonSafe(res);
      if (!res.ok) return { data: null, error: json?.error || 'Failed to save capacity' };
      return { data: (json?.data || null) as WorkloadCapacity | null, error: null };
    } catch (e: any) {
      return { data: null, error: e?.message || 'Network error' };
    }
  },

  async listAllocations(params: { organization_id: string; user_id?: string; project_id?: string; from?: string; to?: string }): Promise<{ data: WorkloadAllocation[]; error: any }> {
    try {
      const token = await getToken();
      const qs = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => { if (v) qs.set(k, String(v)); });

      const res = await fetch(`${API_BASE_URL}/allocations?${qs.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const json = await parseJsonSafe(res);
      if (!res.ok) return { data: [], error: json?.error || 'Failed to fetch allocations' };
      return { data: (json?.data || []) as WorkloadAllocation[], error: null };
    } catch (e: any) {
      return { data: [], error: e?.message || 'Network error' };
    }
  },

  async createAllocation(params: {
    organization_id: string;
    user_id: string;
    project_id?: string | null;
    start_date: string;
    end_date: string;
    allocation_percent?: number;
    allocation_hours?: number;
    notes?: string | null;
  }): Promise<{ data: WorkloadAllocation | null; error: any }> {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/allocations`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });
      const json = await parseJsonSafe(res);
      if (!res.ok) return { data: null, error: json?.error || 'Failed to create allocation' };
      return { data: (json?.data || null) as WorkloadAllocation | null, error: null };
    } catch (e: any) {
      return { data: null, error: e?.message || 'Network error' };
    }
  },

  async updateAllocation(allocationId: string, organizationId: string, updates: Partial<WorkloadAllocation>): Promise<{ data: WorkloadAllocation | null; error: any }> {
    try {
      const token = await getToken();
      const qs = new URLSearchParams({ organization_id: organizationId });
      const res = await fetch(`${API_BASE_URL}/allocations/${encodeURIComponent(allocationId)}?${qs.toString()}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...updates, organization_id: organizationId }),
      });
      const json = await parseJsonSafe(res);
      if (!res.ok) return { data: null, error: json?.error || 'Failed to update allocation' };
      return { data: (json?.data || null) as WorkloadAllocation | null, error: null };
    } catch (e: any) {
      return { data: null, error: e?.message || 'Network error' };
    }
  },

  async deleteAllocation(allocationId: string, organizationId: string): Promise<{ error: any }> {
    try {
      const token = await getToken();
      const qs = new URLSearchParams({ organization_id: organizationId });
      const res = await fetch(`${API_BASE_URL}/allocations/${encodeURIComponent(allocationId)}?${qs.toString()}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const json = await parseJsonSafe(res);
      if (!res.ok) return { error: json?.error || 'Failed to delete allocation' };
      return { error: null };
    } catch (e: any) {
      return { error: e?.message || 'Network error' };
    }
  },
};
